import type { FormacaoFrequencia, MotivoJustificar } from './types';

// Os cinco motivos de falta, na ordem que aparecem nos chips.
export const MOTIVOS_JUSTIFICAR: { valor: MotivoJustificar; rotulo: string }[] = [
  { valor: 'trabalho', rotulo: 'Trabalho' },
  { valor: 'saude', rotulo: 'Saúde' },
  { valor: 'viagem', rotulo: 'Viagem' },
  { valor: 'familia', rotulo: 'Família' },
  { valor: 'outro', rotulo: 'Outro' },
];

// Data curta dd/mm a partir de um ISO.
export function ddmm(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

// Hora HH:MM — aceita ISO completo ou já uma hora pronta.
export function hhmm(v: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mi}`;
  }
  const m = v.match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : v;
}

// Telefone bonito: (DD) 9XXXX-XXXX. Some com o 55 quando vier junto.
export function formatarTelefone(tel: string | null): string {
  let d = (tel || '').replace(/\D/g, '');
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return tel || '';
}

// Link do WhatsApp. Não repete o 55 quando ele já está no número.
export function linkWhatsapp(tel: string | null): string | null {
  const d = (tel || '').replace(/\D/g, '');
  if (!d) return null;
  const numero = d.startsWith('55') && (d.length === 12 || d.length === 13) ? d : `55${d}`;
  return `https://wa.me/${numero}`;
}

// Motivo da falta por extenso.
export function motivoJustificarTexto(motivo: string | null, obs: string | null): string {
  switch ((motivo || '').toLowerCase()) {
    case 'trabalho':
      return 'Trabalho';
    case 'saude':
      return 'Saúde';
    case 'viagem':
      return 'Viagem';
    case 'familia':
      return 'Família';
    case 'outro':
      return (obs || '').trim() || 'Outro';
    default:
      return 'Justificada';
  }
}

// Motivo de estar fora da montagem por extenso.
export function motivoServirTexto(motivo: string | null, obs: string | null): string {
  switch ((motivo || '').toLowerCase()) {
    case 'nao_veio':
      return 'Não veio às formações';
    case 'pediu':
      return 'Pediu para não servir';
    case 'outro':
      return (obs || '').trim() || 'Outro';
    default:
      return 'Fora da montagem';
  }
}

// Detalhe da presença confirmada, conforme como foi registrada.
export function textoPresente(f: { como: string | null; hora: string | null }): string {
  const h = hhmm(f.hora);
  if (f.como === 'qr') return h ? `Leu o QR às ${h}` : 'Leu o QR';
  if (f.como === 'foto') return h ? `Foto aprovada · ${h}` : 'Foto aprovada';
  if (f.como === 'direcao') return h ? `Marcada pela direção · ${h}` : 'Marcada pela direção';
  return h;
}

// Nome de quem registrou/aprovou; "conta sem nome" quando vier vazio.
export function nomeOu(nome: string | null | undefined, padrao = 'conta sem nome'): string {
  const t = (nome || '').trim();
  return t || padrao;
}

// Data e hora curtas (dd/mm HH:MM) a partir de um ISO.
export function dataHoraCurta(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return hhmm(iso);
  const dia = ddmm(iso);
  const h = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return dia ? `${dia} ${h}` : h;
}

// Presença cancelada pela direção (o motivo fica em presencas.observacao).
const PREFIXO_CANCELADA = 'Cancelada pela direção:';

export function ehPresencaCancelada(f: {
  situacao: string | null;
  presenca_observacao?: string | null;
}): boolean {
  return (
    f.situacao === 'recusada' &&
    (f.presenca_observacao || '').trim().startsWith(PREFIXO_CANCELADA)
  );
}

export function motivoCancelamento(f: { presenca_observacao?: string | null }): string {
  const t = (f.presenca_observacao || '').trim();
  return t.startsWith(PREFIXO_CANCELADA) ? t.slice(PREFIXO_CANCELADA.length).trim() : t;
}

// Quem marcou ou aprovou a presença confirmada, com nome e hora.
export function textoQuemMarcou(f: FormacaoFrequencia): string {
  if (f.como === 'qr') {
    const h = hhmm(f.registrada_em || f.hora);
    return h ? `Pelo QR, na conta dela · ${h}` : 'Pelo QR, na conta dela';
  }
  if (f.como === 'foto') {
    const quem = (f.avaliada_por_nome || '').trim();
    if (!quem) return textoPresente(f);
    const quando = dataHoraCurta(f.avaliada_em || f.hora);
    return `Aprovada por ${quem}${quando ? ` · ${quando}` : ''}`;
  }
  if (f.como === 'direcao') {
    const quem = (f.registrada_por_nome || '').trim();
    if (!quem) return textoPresente(f);
    const quando = dataHoraCurta(f.registrada_em || f.hora);
    let t = `Marcada pela direção: ${quem}${quando ? ` · ${quando}` : ''}`;
    const aprov = (f.avaliada_por_nome || '').trim();
    if (aprov && quem !== aprov) t += ` · aprovada por ${aprov}`;
    return t;
  }
  return textoPresente(f);
}